/**
 * CHOOSE YOUR COLOR — the Profile panel: the 20-tile hue strip with the
 * CUSTOM tile, the PICKED row, and the drop-image row.
 * `design/GT Crew.dc.html` (desktop geometry) / `design/GT Mobile Crew.dc.html`
 * (phone geometry), DESIGN-CHANGELOG 2026-08-18 + its two amendments.
 *
 * One component, `desktop` prop — the `MediaCarousel` precedent — because the
 * two trees differ only in geometry:
 *   desktop  tiles `flex:1 1 0; aspect-ratio:1` fill the panel's width (~33px
 *            at 860px, no scroller, nothing hidden), 4px gaps, 26px PICKED
 *   phone    a fixed 44px swipe rail (`data-hs` chrome-less scroller), 6px
 *            gaps, 30px PICKED
 *
 * Every tile previews YOUR initial at that hue; the ✓ marks the current pick.
 * Colors are NOT exclusive — the tip (`ORANGE — ALSO DAVE`) and the PICKED
 * row say who is on one, they never block a pick. The CUSTOM tile previews
 * the field itself: the same two CSS gradients the mirrors ship (hue wheel
 * across, pale-over-deep down).
 */

import type { CSSProperties } from 'react';

import { useQd } from '../store/index.ts';
import { characterVM } from '../store/selectors.x2.ts';

const CUSTOM_BG =
  'linear-gradient(to bottom,rgba(255,255,255,.7),rgba(255,255,255,0) 45%,rgba(0,0,0,0) 55%,rgba(0,0,0,.7)),' +
  'linear-gradient(to right,oklch(0.62 0.34 0),oklch(0.62 0.34 60),oklch(0.62 0.34 120),oklch(0.62 0.34 180),oklch(0.62 0.34 240),oklch(0.62 0.34 300),oklch(0.62 0.34 360))';

export function CharacterPanel({ desktop }: { desktop?: boolean }): JSX.Element {
  const s = useQd();
  const v = characterVM(s);

  const tile: CSSProperties = desktop
    ? { position: 'relative', flex: '1 1 0', minWidth: 0, aspectRatio: '1', boxSizing: 'border-box' }
    : { position: 'relative', flex: 'none', width: '44px', height: '44px', boxSizing: 'border-box' };
  const iniFs = desktop ? '9px' : '12px';
  const checkFs = desktop ? '7.5px' : '9px';
  const checkPos: CSSProperties = desktop ? { top: 1, right: 2 } : { top: 2, right: 3 };
  const wornPx = desktop ? '26px' : '30px';
  const wornFs = desktop ? '8px' : '9px';
  const pad = desktop ? '16px' : '14px';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border)',
        background: 'var(--gt-panel)',
        boxShadow: 'var(--gt-shadow-card)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '10px',
          background: 'var(--gt-raised)',
          borderBottom: '1px solid var(--gt-border)',
          padding: '12px ' + pad,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '8px', color: 'var(--gt-text)' }}>
          CHOOSE YOUR COLOR
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: pad }}>
        <div
          data-hs={desktop ? undefined : '1'}
          style={
            desktop
              ? { display: 'flex', gap: '4px', alignItems: 'stretch' }
              : { display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto' }
          }
        >
          <div
            onClick={s.openCc}
            data-tip={v.ccTip}
            style={{
              ...tile,
              border: '1px solid ' + v.ccBd,
              boxShadow: v.ccSh,
              background: CUSTOM_BG,
              cursor: 'pointer',
              overflow: 'hidden',
            }}
          >
            {v.ccIsMine ? (
              <span
                style={{
                  position: 'absolute',
                  ...checkPos,
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: checkFs,
                  lineHeight: 1,
                  color: 'var(--gt-text)',
                }}
              >
                ✓
              </span>
            ) : null}
          </div>
          {v.charTiles.map((c) => (
            <div
              key={c.key}
              onClick={() => void s.setMyHue(c.h)}
              data-tip={c.tip}
              style={{
                ...tile,
                border: '1px solid ' + c.bd,
                background: c.hueBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: c.sh,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: iniFs,
                  color: c.ink,
                  transform: 'translateY(0.13em)',
                }}
              >
                {c.ini}
              </span>
              {c.isMine ? (
                <span
                  style={{
                    position: 'absolute',
                    ...checkPos,
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: checkFs,
                    lineHeight: 1,
                    color: 'var(--gt-ink)',
                  }}
                >
                  ✓
                </span>
              ) : null}
            </div>
          ))}
        </div>
        {v.hasWorn ? (
          <div style={{ display: 'flex', gap: desktop ? '8px' : '7px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '8px',
                letterSpacing: '.08em',
                color: 'var(--gt-muted-2)',
              }}
            >
              PICKED
            </span>
            {v.wornRows.map((w) => (
              <div
                key={w.key}
                data-tip={w.tip}
                style={{
                  position: 'relative',
                  width: wornPx,
                  height: wornPx,
                  flex: 'none',
                  boxSizing: 'border-box',
                  border: '1px solid ' + w.bd,
                  background: w.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--gt-font-pixel)',
                    fontSize: wornFs,
                    color: w.ink,
                    transform: 'translateY(0.13em)',
                  }}
                >
                  {w.ini}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {v.charHasAv ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              border: '1px dashed var(--gt-border-2)',
              padding: '11px 13px',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9.5px',
                lineHeight: 1.7,
                color: 'var(--gt-muted)',
                flex: 1,
                minWidth: '180px',
              }}
            >
              YOUR UPLOADED IMAGE SITS OVER THE TILE — DROP IT TO SHOW THE COLOR AGAIN.
            </span>
            <span
              onClick={() => void s.dropAv()}
              style={{
                border: '1px solid var(--gt-border-2)',
                color: 'var(--gt-text-3)',
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '10px',
                padding: desktop ? '8px 11px' : '12px 11px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              USE THE COLOR ✕
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
